/**
 * JJ custom: <jj-seating-calculator>  (James 3.9.0)
 *
 * Estimates how many chairs fit around a dining table, using the fixed size
 * options James & James sells (pill buttons from
 * snippets/seating-calculator.liquid).
 *
 * Method: each chair needs its own width plus clearance to its neighbor:
 * 6" for comfortable seating, 2" for a cozy maximum.
 *   - Rectangular: counted BETWEEN THE LEGS.
 *       long side = floor((length - sideInset) / (chair + gap))   sideInset default 14"
 *       end       = floor((width  - endInset)  / (chair + gap))   endInset  default 12"
 *   - Square: chairs per side = floor(side / (chair + gap))
 *   - Round: floor(circumference / (chair + gap))
 *   - Oval: Ramanujan ellipse perimeter x 0.85 usable factor (curved ends
 *     seat less efficiently), then the same division.
 *
 * 3.9.0 additions:
 *   - Prefill: each time the pop-up opens, the shape and size are set from
 *     the options selected on the PDP. Values are read from Hyper's
 *     [data-selected-swatch-value="<Option>"] labels or the
 *     select[name="options[<Option>]"] dropdowns in the product section,
 *     falling back to the variant the page loaded with.
 *   - Chair select: typical chair / J&J chairs (width parsed from the
 *     Specifications accordion text) / custom width.
 *   - Results update live; no Calculate button.
 *
 * 3.10.0 — add to cart from the pop-up (J&J chairs only):
 *   - Chair data + Acumatica line-item properties are fetched from
 *     <chair url>?view=jj-cart-props (templates/product.jj-cart-props.liquid,
 *     built by snippets/jj-cart-properties.liquid with the same rules as
 *     buy-buttons.liquid). Native Combined Listings chairs return their finish
 *     choices; the chosen finish's CHILD product is fetched the same way, so
 *     the parent's placeholder variants are never added.
 *   - "Add chairs to cart": chair line only.
 *   - "Add table + chairs": the table line is built from this PDP's product
 *     form by window.jjCart.buildCartAddBody (exported by
 *     assets/jj-line-item-properties.js — the same builder the Add to cart
 *     button uses, incl. selected add-ons), plus the chair line, in ONE
 *     /cart/add.js request so a dining-set discount sees both together.
 *   - Afterwards: cart count + cart drawer contents refresh in the background
 *     (pubsub cartUpdate); the pop-up stays open with a confirmation and the
 *     discount the cart actually applied to the added lines. No drawer, no
 *     redirect.
 *
 * 3.11.0:
 *   - Chair picker with thumbnails: the native <select data-chair-select>
 *     stays the source of truth (all logic reads it); a listbox-style picker
 *     (thumbnail, title, width) is built from its options and drives it.
 *   - Chair-option load failures show inline under Finish. If the
 *     jj-cart-props view returns HTML instead of JSON, the theme is missing
 *     templates/product.jj-cart-props.liquid; that is logged to the console
 *     and spelled out in the theme editor.
 *
 * 3.11.1: the jj-cart-props view is requested with Accept: text/html. With
 *   Accept: application/json Shopify returns its own product JSON instead of
 *   rendering ?view= (finish list came back empty). Responses that aren't the
 *   view's {type: variants|combined} format now raise the inline load error.
 *
 * 3.11.2: after a successful add the green confirmation shows for 5 seconds,
 *   then the pop-up closes and the cart drawer opens (refreshed via
 *   cart:refresh {open:true}). If the shopper closes the pop-up themselves
 *   during those 5 seconds, the cart drawer opens right away. Without a cart
 *   drawer (cart type "page") the pop-up just closes. A new add restarts it.
 *
 * Defined as a custom element so it re-initializes when the product section
 * is re-rendered (e.g. combined-listing swatch swaps).
 */
(function () {
  if (customElements.get('jj-seating-calculator')) return;

  var COMFORT_GAP = 6;
  var COZY_GAP = 2;

  function list(str) {
    return String(str || '')
      .split(',')
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);
  }

  /**
   * Option value -> inches. "7'" / "7 ft" / "7.5'" -> feet; '42"' / "42 in" -> inches.
   * A bare number is read with the group's default unit.
   */
  function toInches(raw, defaultUnit) {
    if (raw == null) return null;
    var s = String(raw).replace(/[’′]/g, "'").replace(/[”″]/g, '"');
    var m = s.match(/(\d+(?:\.\d+)?)\s*(ft|feet|foot|'|in|inch|inches|")?/i);
    if (!m) return null;
    var n = parseFloat(m[1]);
    var unit = (m[2] || '').toLowerCase();
    if (unit === "'" || unit.indexOf('f') === 0) return Math.round(n * 12);
    if (unit === '"' || unit.indexOf('in') === 0) return Math.round(n);
    return Math.round(defaultUnit === 'ft' ? n * 12 : n);
  }

  /**
   * Overall chair width from the Specifications accordion text, e.g.
   *   'Overall Dimensions:\n19"W, 21"D, 34.5"H'
   *   'Overall (Open): 17"W x 24"D x 32"H'
   * Takes the first `NN"W` after "Overall" (so the narrower seat width is
   * ignored). Falls back to the first `NN"W` anywhere.
   */
  function parseChairWidth(text) {
    if (!text) return null;
    var s = String(text).replace(/[”″]/g, '"');
    var re = /(\d+(?:\.\d+)?)\s*(?:"|in\.?|inches)?\s*W\b/i;
    var idx = s.search(/overall/i);
    var m = idx >= 0 ? s.slice(idx).match(re) : null;
    if (!m) m = s.match(re);
    if (!m) return null;
    var w = parseFloat(m[1]);
    return w >= 10 && w <= 48 ? w : null;
  }

  function fmt(n) {
    return (Math.round(n * 10) / 10).toString();
  }

  function money(cents) {
    try {
      if (window.FoxTheme && FoxTheme.Currency && FoxTheme.settings) {
        return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return '$' + (cents / 100).toFixed(2);
  }

  var ICON_CHAIR =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M7 3h10v8H7zM6 11h12v3H6zM7 14v7M17 14v7"/></svg>';
  var ICON_RULER =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 15 15 3l6 6L9 21z"/><path d="M7 11l2 2M10 8l2 2M13 5l2 2"/></svg>';

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function thumbHtml(opt) {
    if (opt.dataset.image) return '<img src="' + esc(opt.dataset.image) + '" alt="" loading="lazy" width="44" height="44">';
    return opt.value === 'custom' ? ICON_RULER : ICON_CHAIR;
  }

  function loadErrorText(err) {
    if (err && err.code === 'TEMPLATE_MISSING') {
      console.error(
        '[seating-calculator] /products/<chair>?view=jj-cart-props returned the normal product page. ' +
          'Add templates/product.jj-cart-props.liquid (from the James 3.10+ build) to this theme.'
      );
      if (window.Shopify && Shopify.designMode) {
        return 'Chair options need the template "templates/product.jj-cart-props.liquid" in this theme. Upload it from the James build, then reload.';
      }
    }
    return "Sorry, we couldn't load this chair's finishes. Use View chair to order it.";
  }

  function viewUrl(url) {
    var u = new URL(url, window.location.origin);
    u.searchParams.delete('variant');
    u.searchParams.set('view', 'jj-cart-props');
    return u.pathname + u.search;
  }

  var propsCache = {};
  function fetchProps(url) {
    var key = viewUrl(url);
    if (!propsCache[key]) {
      // Accept text/html on purpose (3.11.1): with "Accept: application/json"
      // Shopify answers a product URL with its built-in product JSON and
      // ignores ?view=, so our template never renders.
      propsCache[key] = fetch(key, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (text) {
          var t = text.trim();
          if (t.charAt(0) === '<') {
            var err = new Error('jj-cart-props view returned HTML');
            err.code = 'TEMPLATE_MISSING';
            throw err;
          }
          var data = JSON.parse(t);
          if (!data || (data.type !== 'variants' && data.type !== 'combined')) {
            var bad = new Error('Unexpected chair data (not the jj-cart-props view)');
            bad.code = data && data.product ? 'SHOPIFY_PRODUCT_JSON' : 'BAD_RESPONSE';
            throw bad;
          }
          return data;
        })
        .catch(function (err) {
          delete propsCache[key];
          throw err;
        });
    }
    return propsCache[key];
  }

  class JJSeatingCalculator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.shape = 'rectangular';
      this.sizes = {};
      this.sideInset = parseFloat(this.dataset.sideInset) || 0;
      this.endInset = parseFloat(this.dataset.endInset) || 0;
      this.shapeButtons = Array.from(this.querySelectorAll('[data-shape]'));
      this.groups = Array.from(this.querySelectorAll('[data-shape-group]'));
      this.resultEl = this.querySelector('[data-result]');
      this.chairSelect = this.querySelector('[data-chair-select]');
      this.customWrap = this.querySelector('[data-custom-chair]');
      this.customInput = this.querySelector('[data-custom-width]');

      // Product section the calculator belongs to. Captured now because the
      // pop-up (and this element) is moved to <body> while it's open.
      var sid = this.dataset.sectionId;
      this.productRoot =
        (sid && document.getElementById('shopify-section-' + sid)) ||
        this.closest('.shopify-section') ||
        document.getElementById('MainContent') ||
        document;

      this.shapeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          this.setShape(btn.dataset.shape);
          this.calculate();
        }.bind(this));
      }, this);

      Array.from(this.querySelectorAll('[data-size-group]')).forEach(function (group) {
        this.bindPills(group);
        var pills = group.querySelectorAll('[data-value]');
        if (pills.length) this.selectSize(group.dataset.sizeGroup, pills[0]);
      }, this);

      this.setupChairs();
      this.setupBuy();

      var reset = this.querySelector('[data-action="reset"]');
      if (reset) reset.addEventListener('click', this.reset.bind(this));

      // Prefill each time the pop-up opens.
      var modal = this.closest('basic-modal, modal-component, drawer-component');
      if (modal) {
        modal.addEventListener('toggle', function () {
          if (modal.hasAttribute('open')) {
            this.prefill();
          } else if (this.pendingCartOpen) {
            // Shopper closed the pop-up before the 5s were up: show the cart now.
            clearTimeout(this.closeTimer);
            this.pendingCartOpen = false;
            this.setBuyMessage('');
            setTimeout(this.openCartDrawer.bind(this), 400);
          }
        }.bind(this));
      }
      this.prefill();
    }

    /* ---------------- sizes ---------------- */

    bindPills(group) {
      var name = group.dataset.sizeGroup;
      Array.from(group.querySelectorAll('[data-value]')).forEach(function (pill) {
        if (pill.dataset.bound) return;
        pill.dataset.bound = '1';
        pill.addEventListener('click', function () {
          this.selectSize(name, pill);
          this.calculate();
        }.bind(this));
      }, this);
    }

    selectSize(name, pill) {
      this.sizes[name] = parseInt(pill.dataset.value, 10);
      var group = pill.closest('[data-size-group]');
      Array.from(group.querySelectorAll('[data-value]')).forEach(function (p) {
        p.setAttribute('aria-pressed', p === pill ? 'true' : 'false');
      });
    }

    /** Select the pill for `inches` in a group, adding it (sorted) if missing. */
    selectInches(name, inches) {
      var group = this.querySelector('[data-size-group="' + name + '"]');
      if (!group || !inches) return false;
      var pills = Array.from(group.querySelectorAll('[data-value]'));
      var pill = pills.find(function (p) { return parseInt(p.dataset.value, 10) === inches; });
      if (!pill) {
        pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'jj-sc__pill';
        pill.dataset.value = String(inches);
        pill.setAttribute('aria-pressed', 'false');
        pill.textContent = group.dataset.unit === 'ft' ? fmt(inches / 12) + "'" : inches + '"';
        var after = pills.find(function (p) { return parseInt(p.dataset.value, 10) > inches; });
        group.insertBefore(pill, after || null);
        this.bindPills(group);
      }
      this.selectSize(name, pill);
      return true;
    }

    setShape(shape) {
      this.shape = shape;
      this.shapeButtons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.dataset.shape === shape ? 'true' : 'false');
      });
      this.groups.forEach(function (group) {
        group.hidden = group.dataset.shapeGroup.split(' ').indexOf(shape) === -1;
      });
    }

    /* ---------------- prefill from the PDP ---------------- */

    /** Selected PDP option values keyed by lower-case option name. */
    readSelectedOptions() {
      var selected = {};
      var root = this.productRoot;

      // 1. Server-rendered selection (variant the page loaded with).
      var json = this.querySelector('[data-sc-selected]');
      if (json) {
        try {
          var data = JSON.parse(json.textContent);
          Object.keys(data).forEach(function (k) { selected[k.toLowerCase()] = data[k]; });
        } catch (e) { /* ignore */ }
      }

      // 2. Live picker state (overrides 1).
      Object.keys(selected).forEach(function (key) {
        var live = null;
        root.querySelectorAll('[data-selected-swatch-value]').forEach(function (el) {
          if (el.getAttribute('data-selected-swatch-value').toLowerCase() === key) {
            var t = el.textContent.trim();
            if (t) live = t;
          }
        });
        if (live == null) {
          root.querySelectorAll('select[name^="options["]').forEach(function (sel) {
            var n = sel.name.slice(8, -1).toLowerCase();
            if (n === key && sel.value) live = sel.value;
          });
        }
        if (live != null) selected[key] = live;
      });
      return selected;
    }

    prefill() {
      var sel = this.readSelectedOptions();
      var pick = function (names) {
        for (var i = 0; i < names.length; i++) {
          if (sel[names[i]] != null && sel[names[i]] !== '') return sel[names[i]];
        }
        return null;
      };
      var lengthVal = pick(list(this.dataset.lengthOption));
      var widthVal = pick(list(this.dataset.widthOption));
      var diaVal = pick(list(this.dataset.diameterOption));

      var shape = (this.dataset.tableShape || 'auto').toLowerCase();
      if (shape === 'auto') {
        if (lengthVal != null && widthVal != null) shape = 'rectangular';
        else if (diaVal != null) shape = 'round';
        else shape = 'rectangular';
      }
      this.setShape(shape);

      if (shape === 'rectangular') {
        this.selectInches('rect_length', toInches(lengthVal, 'ft'));
        this.selectInches('rect_width', toInches(widthVal, 'in'));
      } else if (shape === 'oval') {
        this.selectInches('oval_length', toInches(lengthVal, 'ft'));
        this.selectInches('oval_width', toInches(widthVal, 'in'));
      } else if (shape === 'round') {
        this.selectInches('diameter', toInches(diaVal, 'in'));
      } else if (shape === 'square') {
        this.selectInches('side', toInches(diaVal != null ? diaVal : widthVal, 'in'));
      }
      this.calculate();
    }

    /* ---------------- chairs ---------------- */

    setupChairs() {
      if (!this.chairSelect) return;
      // Resolve J&J chair widths; drop chairs whose width can't be read.
      Array.from(this.chairSelect.options).forEach(function (opt) {
        if (opt.value.indexOf('chair-') !== 0) return;
        var w = parseChairWidth(opt.dataset.dimensions);
        if (!w) {
          opt.remove();
          return;
        }
        opt.dataset.width = String(w);
        opt.textContent = (opt.dataset.chairTitle || opt.textContent) + ' (' + fmt(w) + '")';
      });
      this.buildChairPicker();

      this.chairSelect.addEventListener('change', function () {
        var custom = this.chairSelect.value === 'custom';
        this.customWrap.hidden = !custom;
        if (custom) this.customInput.focus();
        this.qtyTouched = false;
        this.calculate();
        this.loadChair();
      }.bind(this));
      this.customInput.addEventListener('input', this.calculate.bind(this));
    }

    /* ---------------- chair picker with thumbnails (3.11.0) ---------------- */

    buildChairPicker() {
      var host = this.querySelector('[data-chair-picker]');
      var select = this.chairSelect;
      if (!host || !select) return;
      var uid = select.id || 'jj-sc-chair';
      var label = this.querySelector('label[for="' + select.id + '"]');

      host.innerHTML =
        '<button type="button" class="jj-sc__picker-btn" aria-haspopup="listbox" aria-expanded="false" aria-controls="' +
        uid + '-list"' + (label ? ' aria-labelledby="' + (label.id || (label.id = uid + '-label')) + ' ' + uid + '-btn"' : '') +
        ' id="' + uid + '-btn"></button>' +
        '<ul class="jj-sc__picker-list" role="listbox" id="' + uid + '-list" tabindex="-1" hidden></ul>';
      this.pickerBtn = host.querySelector('.jj-sc__picker-btn');
      this.pickerList = host.querySelector('.jj-sc__picker-list');

      Array.from(select.options).forEach(function (opt, i) {
        var li = document.createElement('li');
        li.className = 'jj-sc__picker-item';
        li.id = uid + '-opt-' + i;
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        var name = opt.dataset.chairTitle || opt.textContent.replace(/\s*\([\d.]+"\)\s*$/, '');
        var meta = opt.dataset.width && opt.value !== 'custom' ? fmt(parseFloat(opt.dataset.width)) + '"' : '';
        li.innerHTML =
          '<span class="jj-sc__thumb">' + thumbHtml(opt) + '</span>' +
          '<span class="jj-sc__picker-name">' + esc(name) + '</span>' +
          (meta ? '<span class="jj-sc__picker-meta">' + meta + '</span>' : '');
        li.addEventListener('click', function () {
          this.choosePickerValue(opt.value);
        }.bind(this));
        this.pickerList.appendChild(li);
      }, this);

      this.pickerBtn.addEventListener('click', function () {
        this.togglePicker(this.pickerList.hidden);
      }.bind(this));
      this.pickerBtn.addEventListener('keydown', this.onPickerKey.bind(this));
      this.pickerList.addEventListener('keydown', this.onPickerKey.bind(this));
      // Keep Escape from also closing the pop-up while the list is open.
      host.addEventListener('keyup', function (e) {
        if (e.key === 'Escape' && this.pickerSwallowEsc) {
          e.stopPropagation();
          this.pickerSwallowEsc = false;
        }
      }.bind(this));
      document.addEventListener('click', function (e) {
        if (!this.pickerList.hidden && !host.contains(e.target)) this.togglePicker(false);
      }.bind(this));
      select.addEventListener('change', this.syncChairPicker.bind(this));

      var nativeWrap = select.closest('.select');
      if (nativeWrap) nativeWrap.style.display = 'none';
      host.hidden = false;
      this.syncChairPicker();
    }

    syncChairPicker() {
      if (!this.pickerBtn) return;
      var select = this.chairSelect;
      var opt = select.options[select.selectedIndex];
      var item = this.pickerList.querySelector('[data-value="' + CSS.escape(opt.value) + '"]');
      this.pickerList.querySelectorAll('[role="option"]').forEach(function (li) {
        li.setAttribute('aria-selected', li === item ? 'true' : 'false');
      });
      this.pickerBtn.innerHTML = item ? item.innerHTML : esc(opt.textContent);
    }

    togglePicker(open) {
      this.pickerList.hidden = !open;
      this.pickerBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        // Open upward when there isn't room below inside the pop-up.
        this.pickerList.classList.remove('is-up');
        var scroller = this.closest('.drawer__body') || document.documentElement;
        var box = scroller.getBoundingClientRect();
        var btn = this.pickerBtn.getBoundingClientRect();
        var listH = Math.min(this.pickerList.scrollHeight, 320);
        if (btn.bottom + listH + 8 > Math.min(box.bottom, window.innerHeight) && btn.top - listH - 8 > Math.max(box.top, 0)) {
          this.pickerList.classList.add('is-up');
        }
        var sel = this.pickerList.querySelector('[aria-selected="true"]') || this.pickerList.firstElementChild;
        this.setPickerActive(sel);
        this.pickerList.focus();
      }
    }

    setPickerActive(li) {
      if (!li) return;
      this.pickerList.querySelectorAll('.is-active').forEach(function (x) { x.classList.remove('is-active'); });
      li.classList.add('is-active');
      this.pickerList.setAttribute('aria-activedescendant', li.id);
      li.scrollIntoView({ block: 'nearest' });
    }

    choosePickerValue(value) {
      this.togglePicker(false);
      this.pickerBtn.focus();
      if (this.chairSelect.value === value) return;
      this.chairSelect.value = value;
      this.chairSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onPickerKey(e) {
      var open = !this.pickerList.hidden;
      var items = Array.from(this.pickerList.querySelectorAll('[role="option"]'));
      var active = this.pickerList.querySelector('.is-active');
      var idx = items.indexOf(active);
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          if (!open) return this.togglePicker(true);
          this.setPickerActive(items[Math.max(0, Math.min(items.length - 1, idx + (e.key === 'ArrowDown' ? 1 : -1)))]);
          break;
        case 'Home':
        case 'End':
          if (!open) return;
          e.preventDefault();
          this.setPickerActive(e.key === 'Home' ? items[0] : items[items.length - 1]);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!open) return this.togglePicker(true);
          if (active) this.choosePickerValue(active.dataset.value);
          break;
        case 'Escape':
          if (open) {
            e.preventDefault();
            this.pickerSwallowEsc = true;
            this.togglePicker(false);
            this.pickerBtn.focus();
          }
          break;
        case 'Tab':
          if (open) this.togglePicker(false);
          break;
      }
    }

    chairWidth() {
      if (!this.chairSelect) return parseFloat(this.dataset.defaultChairWidth) || 19;
      if (this.chairSelect.value === 'custom') {
        var v = parseFloat(this.customInput.value);
        return v > 0 ? v : null;
      }
      var opt = this.chairSelect.options[this.chairSelect.selectedIndex];
      return parseFloat(opt && opt.dataset.width) || parseFloat(this.dataset.defaultChairWidth) || 19;
    }

    /* ---------------- math ---------------- */

    perSide(len, chairWidth, gap) {
      return Math.max(0, Math.floor(len / (chairWidth + gap)));
    }

    countFor(gap, cw) {
      switch (this.shape) {
        case 'rectangular': {
          var l = this.perSide(this.sizes.rect_length - this.sideInset, cw, gap);
          var w = this.perSide(this.sizes.rect_width - this.endInset, cw, gap);
          return { total: 2 * l + 2 * w, long: l, end: w };
        }
        case 'square': {
          var s = this.perSide(this.sizes.side, cw, gap);
          return { total: 4 * s, side: s };
        }
        case 'round': {
          var circumference = Math.PI * this.sizes.diameter;
          return { total: Math.max(0, Math.floor(circumference / (cw + gap))) };
        }
        case 'oval': {
          var a = this.sizes.oval_length / 2;
          var b = this.sizes.oval_width / 2;
          var perimeter = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
          return { total: Math.max(0, Math.floor((perimeter * 0.85) / (cw + gap))) };
        }
      }
      return { total: 0 };
    }

    calculate() {
      var cw = this.chairWidth();
      if (!cw || cw < 10 || cw > 48) {
        this.hideResult();
        return;
      }
      var comfy = this.countFor(COMFORT_GAP, cw);
      var cozy = this.countFor(COZY_GAP, cw);
      var cozyTotal = Math.max(cozy.total, comfy.total);

      var breakdown = '';
      var note = '';
      if (this.shape === 'rectangular') {
        breakdown = comfy.long + ' per long side · ' + comfy.end + ' per end';
        if (this.sideInset || this.endInset) {
          note =
            'Measured between the legs: ' +
            fmt(Math.max(0, this.sizes.rect_length - this.sideInset)) + '" per long side, ' +
            fmt(Math.max(0, this.sizes.rect_width - this.endInset)) + '" per end.';
        }
      } else if (this.shape === 'square') {
        breakdown = comfy.side + ' per side';
      } else {
        breakdown = 'evenly spaced around the table';
      }

      this.querySelector('[data-result-number]').textContent = comfy.total;
      this.querySelector('[data-result-caption]').textContent =
        comfy.total === 1 ? 'chair fits comfortably' : 'chairs fit comfortably';
      this.querySelector('[data-result-breakdown]').textContent = breakdown;
      this.querySelector('[data-result-max]').textContent =
        cozyTotal > comfy.total ? 'Up to ' + cozyTotal + ' with cozier spacing' : '';
      var noteEl = this.querySelector('[data-result-note]');
      if (noteEl) noteEl.textContent = note;
      this.resultEl.hidden = false;
      this.lastComfy = comfy.total;
      this.syncBuyPanel();
      this.announceResult(comfy.total, cozyTotal, cw);
    }

    /**
     * Analytics hook (3.7.11): tell listeners (assets/jj-analytics-events.js)
     * what the calculator just showed. Only while the pop-up is open, so the
     * prefill on page load doesn't count as a shopper result. Debounce and
     * de-dupe live in the listener.
     */
    announceResult(comfortable, cozy, chairWidth) {
      var modal = this.closest('basic-modal, modal-component, drawer-component');
      if (modal && !modal.hasAttribute('open')) return;
      var s = this.sizes || {};
      var sizes;
      if (this.shape === 'rectangular') sizes = { length: s.rect_length, width: s.rect_width };
      else if (this.shape === 'square') sizes = { side: s.side };
      else if (this.shape === 'oval') sizes = { length: s.oval_length, width: s.oval_width };
      else sizes = { diameter: s.diameter };
      var chairOpt = this.selectedChairOption ? this.selectedChairOption() : null;
      document.dispatchEvent(new CustomEvent('seating-calculator:result', {
        detail: {
          shape: this.shape,
          sizes: sizes,
          chair_width: chairWidth,
          chair: chairOpt ? chairOpt.textContent.trim() : null,
          comfortable: comfortable,
          cozy: cozy,
        },
      }));
    }

    reset() {
      if (this.chairSelect) {
        this.chairSelect.selectedIndex = 0;
        this.customWrap.hidden = true;
        this.customInput.value = this.dataset.defaultChairWidth || 19;
        this.syncChairPicker();
      }
      this.qtyTouched = false;
      this.setBuyMessage('');
      this.prefill();
      this.loadChair();
    }

    /* ---------------- add to cart (3.10.0) ---------------- */

    setupBuy() {
      this.buy = this.querySelector('[data-buy]');
      this.buyHint = this.querySelector('[data-buy-hint]');
      if (!this.buy || this.dataset.enableBuy === 'false') {
        this.buy = null;
        return;
      }
      this.buyTitle = this.buy.querySelector('[data-buy-title]');
      this.buyThumb = this.buy.querySelector('[data-buy-thumb]');
      this.loadErrEl = this.buy.querySelector('[data-buy-load-error]');
      this.buyLink = this.buy.querySelector('[data-buy-link]');
      this.finishWrap = this.buy.querySelector('[data-buy-finish-wrap]');
      this.finishLabel = this.buy.querySelector('[data-buy-finish-label]');
      this.finishSelect = this.buy.querySelector('[data-buy-finish]');
      this.variantWrap = this.buy.querySelector('[data-buy-variant-wrap]');
      this.variantSelect = this.buy.querySelector('[data-buy-variant]');
      this.qtyInput = this.buy.querySelector('[data-buy-qty]');
      this.priceEl = this.buy.querySelector('[data-buy-price]');
      this.btnChairs = this.buy.querySelector('[data-buy-action="chairs"]');
      this.btnBundle = this.buy.querySelector('[data-buy-action="bundle"]');
      this.msgEl = this.buy.querySelector('[data-buy-message]');
      this.chairData = null; // { product, variants, choiceUrl }
      this.qtyTouched = false;
      this.busy = false;

      this.finishSelect.addEventListener('change', this.onFinishChange.bind(this));
      this.variantSelect.addEventListener('change', this.renderVariant.bind(this));
      this.qtyInput.addEventListener('input', function () {
        this.qtyTouched = true;
        this.renderVariant();
      }.bind(this));
      this.buy.querySelectorAll('[data-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var q = this.qty() + parseInt(btn.dataset.qtyStep, 10);
          this.qtyInput.value = Math.max(1, Math.min(24, q));
          this.qtyTouched = true;
          this.renderVariant();
        }.bind(this));
      }, this);
      this.btnChairs.addEventListener('click', this.addToCart.bind(this, false));
      this.btnBundle.addEventListener('click', this.addToCart.bind(this, true));

      this.loadChair();
    }

    selectedChairOption() {
      if (!this.chairSelect) return null;
      var opt = this.chairSelect.options[this.chairSelect.selectedIndex];
      return opt && opt.value.indexOf('chair-') === 0 && opt.dataset.url ? opt : null;
    }

    /** Show/hide the panel and keep the quantity on the comfortable count. */
    syncBuyPanel() {
      if (!this.buy) return;
      var opt = this.selectedChairOption();
      this.buy.hidden = !opt;
      if (this.buyHint) this.buyHint.hidden = !!opt;
      if (opt && !this.qtyTouched && this.lastComfy) {
        this.qtyInput.value = Math.max(1, Math.min(24, this.lastComfy));
      }
      this.renderVariant();
    }

    qty() {
      var q = parseInt(this.qtyInput && this.qtyInput.value, 10);
      return Number.isNaN(q) ? 1 : Math.max(1, Math.min(24, q));
    }

    loadChair() {
      if (!this.buy) return;
      var opt = this.selectedChairOption();
      this.chairData = null;
      this.syncBuyPanel();
      if (!opt) return;

      var title = opt.dataset.chairTitle || opt.textContent;
      this.buyTitle.textContent = title;
      this.buyLink.href = opt.dataset.url;
      if (this.buyThumb) {
        this.buyThumb.innerHTML = thumbHtml(opt);
        this.buyThumb.hidden = !opt.dataset.image;
      }
      this.setLoadError('');
      this.finishSelect.innerHTML = '<option>Loading…</option>';
      this.finishSelect.disabled = true;
      this.variantWrap.hidden = true;
      this.setBuyMessage('');
      this.renderVariant();

      var token = (this.loadToken = {});
      fetchProps(opt.dataset.url)
        .then(function (data) {
          if (token !== this.loadToken) return;
          this.finishSelect.innerHTML = '';
          this.finishSelect.disabled = false;
          if (data.type === 'combined' && !(data.choices && data.choices.length)) {
            throw new Error('No finishes returned for this chair');
          }
          if (data.type === 'variants' && !(data.variants && data.variants.length)) {
            throw new Error('No variants returned for this chair');
          }
          if (data.type === 'combined') {
            // Native Combined Listings: one choice per finish -> child product.
            this.finishLabel.textContent = data.option || 'Finish';
            this.finishWrap.hidden = false;
            var preferred = -1;
            data.choices.forEach(function (c, i) {
              var o = document.createElement('option');
              o.value = c.url;
              o.textContent = c.label + (c.available === false ? ' (sold out)' : '');
              this.finishSelect.appendChild(o);
              if (preferred === -1 && c.available !== false) preferred = i;
            }, this);
            this.finishSelect.selectedIndex = Math.max(0, preferred);
            this.chairMode = 'combined';
            this.onFinishChange();
          } else {
            this.chairMode = 'variants';
            this.useProduct(data, true);
          }
        }.bind(this))
        .catch(function (err) {
          if (token !== this.loadToken) return;
          console.error('[seating-calculator]', err);
          this.finishSelect.innerHTML = '<option value="">Unavailable</option>';
          this.finishSelect.disabled = true;
          this.setLoadError(loadErrorText(err));
          this.renderVariant();
        }.bind(this));
    }

    setLoadError(text) {
      if (this.loadErrEl) this.loadErrEl.textContent = text || '';
    }

    onFinishChange() {
      if (this.chairMode === 'combined') {
        this.setLoadError('');
        var url = this.finishSelect.value;
        var token = (this.loadToken = {});
        this.chairData = null;
        this.buyLink.href = url;
        this.renderVariant();
        fetchProps(url)
          .then(function (child) {
            if (token !== this.loadToken) return;
            if (child.type !== 'variants') throw new Error('Unexpected child product');
            this.useProduct(child, false);
          }.bind(this))
          .catch(function (err) {
            if (token !== this.loadToken) return;
            console.error('[seating-calculator]', err);
            this.setLoadError(loadErrorText(err));
            this.renderVariant();
          }.bind(this));
      } else {
        this.renderVariant();
      }
    }

    /**
     * variants mode (regular chair): finish select = the product's variants.
     * combined mode (child product): optional "Option" select if the child
     * has more than one variant.
     */
    useProduct(data, asFinish) {
      this.chairData = data;
      var variants = data.variants || [];
      var target = asFinish ? this.finishSelect : this.variantSelect;
      target.innerHTML = '';
      var preferred = -1;
      variants.forEach(function (v, i) {
        var o = document.createElement('option');
        o.value = String(v.id);
        o.textContent = v.title + (v.available ? '' : ' (sold out)');
        target.appendChild(o);
        if (preferred === -1 && v.available) preferred = i;
      });
      target.selectedIndex = Math.max(0, preferred);
      if (asFinish) {
        var single = variants.length === 1 && /default title/i.test(variants[0].title);
        this.finishWrap.hidden = single;
        this.finishLabel.textContent =
          (data.options && data.options.length === 1 && data.options[0]) || 'Finish';
        this.variantWrap.hidden = true;
      } else {
        this.variantWrap.hidden = variants.length < 2;
      }
      this.renderVariant();
    }

    currentVariant() {
      if (!this.chairData) return null;
      var id = this.chairMode === 'combined' ? this.variantSelect.value : this.finishSelect.value;
      var variants = this.chairData.variants || [];
      if (this.chairMode === 'combined' && variants.length === 1) return variants[0];
      return variants.find(function (v) { return String(v.id) === String(id); }) || null;
    }

    tableForm() {
      var id = this.dataset.productFormId;
      var form = id ? document.getElementById(id) : null;
      return form || (this.productRoot.querySelector && this.productRoot.querySelector('form[is="product-form"]'));
    }

    tableAvailable() {
      var form = this.tableForm();
      if (!form) return false;
      var idInput = form.querySelector('[name="id"]');
      if (!idInput || !idInput.value || idInput.disabled) return false;
      var submit = form.querySelector('[type="submit"]');
      return !(submit && (submit.disabled || submit.getAttribute('aria-disabled') === 'true'));
    }

    renderVariant() {
      if (!this.buy || this.buy.hidden) return;
      var v = this.currentVariant();
      var q = this.qty();
      var chairOk = !!(v && v.available);

      if (v) {
        var line = money(v.price) + ' each';
        if (v.compare_at_price && v.compare_at_price > v.price) line += ' <s>' + money(v.compare_at_price) + '</s>';
        line += ' · <strong>' + money(v.price * q) + '</strong> for ' + q;
        if (!v.available) line += ' · <strong>Sold out</strong>';
        this.priceEl.innerHTML = line;
      } else {
        this.priceEl.textContent = '';
      }

      var noun = q === 1 ? 'chair' : 'chairs';
      this.buy.querySelector('[data-buy-label-chairs]').textContent = 'Add ' + q + ' ' + noun + ' to cart';
      this.buy.querySelector('[data-buy-label-bundle]').textContent = 'Add table + ' + q + ' ' + noun;

      this.btnChairs.setAttribute('aria-disabled', chairOk && !this.busy ? 'false' : 'true');
      this.btnBundle.setAttribute('aria-disabled', chairOk && !this.busy && this.tableAvailable() ? 'false' : 'true');
      this.btnBundle.title = this.tableAvailable() ? '' : 'Choose an available table size on the page first';
    }

    /** 5s after a successful add: close the pop-up, then open the cart drawer. */
    openCartDrawer() {
      if (!document.querySelector('cart-drawer')) return; // cart type "page": nothing to open
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    }

    scheduleCloseToCart() {
      clearTimeout(this.closeTimer);
      var self = this;
      this.pendingCartOpen = true;
      this.closeTimer = setTimeout(function () {
        self.pendingCartOpen = false;
        var modal = self.closest('basic-modal, modal-component, drawer-component');
        // Open the drawer once: when the pop-up finishes hiding, or after
        // 700ms if its hide transition never reports back (e.g. reduced motion).
        var opened = false;
        var openDrawer = function () {
          if (opened) return;
          opened = true;
          self.openCartDrawer();
        };
        if (modal && modal.hasAttribute('open') && typeof modal.hide === 'function') {
          var hidden = modal.hide();
          if (hidden && typeof hidden.then === 'function') hidden.then(openDrawer);
          setTimeout(openDrawer, 700);
        } else {
          openDrawer();
        }
        self.setBuyMessage('');
      }, 5000);
    }

    setBuyMessage(text, state) {
      if (!this.msgEl) return;
      this.msgEl.textContent = text || '';
      this.msgEl.dataset.state = state || '';
    }

    addToCart(withTable) {
      var btn = withTable ? this.btnBundle : this.btnChairs;
      if (this.busy || btn.getAttribute('aria-disabled') === 'true') {
        if (withTable && !this.tableAvailable()) {
          this.setBuyMessage('Choose an available table size on the page first.', 'error');
        }
        return;
      }
      var v = this.currentVariant();
      if (!v) return;
      var q = this.qty();
      var chairItem = { id: v.id, quantity: q, properties: v.properties || {} };

      var sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
      );

      var body;
      var tableTitle = '';
      if (withTable) {
        var form = this.tableForm();
        if (!form || !window.jjCart || typeof window.jjCart.buildCartAddBody !== 'function') {
          this.setBuyMessage("Sorry, we couldn't add the table from here. Please use Add to cart on the page.", 'error');
          return;
        }
        var extra = (window.jjCart.collectAddonItems ? window.jjCart.collectAddonItems(form) : []).concat([chairItem]);
        body = window.jjCart.buildCartAddBody(new FormData(form), sections, window.location.pathname, extra);
        if (!body) {
          this.setBuyMessage('Choose an available table size on the page first.', 'error');
          return;
        }
        var titleEl = document.querySelector('.product__title h1, h1.product__title, .product__title');
        tableTitle = titleEl ? titleEl.textContent.trim() : 'table';
      } else {
        body = { items: [chairItem], sections: sections, sections_url: window.location.pathname };
      }

      clearTimeout(this.closeTimer);
      this.pendingCartOpen = false;
      this.busy = true;
      btn.classList.add('btn--loading');
      this.setBuyMessage('');
      this.renderVariant();

      var chairName = (this.chairData && this.chairData.title) || this.buyTitle.textContent;
      // Regular chairs: show the chosen variant (finish). Combined Listings
      // children already carry the finish in their title.
      var finish = v.title && !/default title/i.test(v.title) && chairName.indexOf(v.title) === -1 ? v.title : '';
      var self = this;
      fetch(FoxTheme.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then(function (res) {
          return res.json().then(function (json) { return { ok: res.ok, json: json }; });
        })
        .then(function (r) {
          if (!r.ok || r.json.status) {
            throw new Error(r.json.description || r.json.message || 'Could not add to cart');
          }
          var added = r.json.items || [];
          return fetch(FoxTheme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
            .then(function (res) { return res.json(); })
            .then(function (cart) {
              cart.sections = r.json.sections;
              if (window.FoxTheme && FoxTheme.pubsub) {
                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
              }
              // Discount the cart actually applied to the lines just added.
              var keys = added.map(function (i) { return i.key; });
              var saved = 0;
              (cart.items || []).forEach(function (line) {
                if (keys.indexOf(line.key) === -1) return;
                saved += line.line_level_total_discount || line.total_discount || 0;
              });
              var chairLabel = q + ' × ' + chairName + (finish ? ' (' + finish + ')' : '');
              var msg = withTable
                ? 'Added to your cart: ' + tableTitle + ' + ' + chairLabel + '.'
                : 'Added to your cart: ' + chairLabel + '.';
              if (saved > 0) msg += ' You saved ' + money(saved) + '.';
              self.setBuyMessage(msg, 'success');
              self.scheduleCloseToCart();
              document.dispatchEvent(new CustomEvent('seating-calculator:added', {
                detail: { withTable: withTable, items: added, cart: cart },
              }));
            });
        })
        .catch(function (err) {
          self.setBuyMessage(err.message || "Sorry, we couldn't add that to your cart.", 'error');
        })
        .finally(function () {
          self.busy = false;
          btn.classList.remove('btn--loading');
          self.renderVariant();
        });
    }

    hideResult() {
      if (this.resultEl) this.resultEl.hidden = true;
    }
  }

  customElements.define('jj-seating-calculator', JJSeatingCalculator);
})();
